import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { usePlausible } from 'next-plausible';
import { useContext, useEffect, useRef, useState } from 'react';
import logoColor from '../../../images/TravelFeed-logo-horizontal-color.svg';
import UserContext from '../../General/UserContext';
import { figtree } from '../../Landing/fonts';
import { PlaneIcon, TfMark } from '../../Landing/ui';

// There is no /login page — login happens in a dialog. Mounted only after
// the first click so the MUI chunk stays off the reading path.
const LoginButton = dynamic(() => import('../../Header/LoginButton'), {
  ssr: false,
});

// Reading navbar for logged-out visitors (members keep the classic Header).
// Minimal chrome in the article's own design language with three jobs:
// a permanent "Start your blog" CTA, a dashed flight-route progress line
// with a little plane riding exactly on it, and a scrollspy that shows
// which chapter of the story you're in.
// Mirrors LegalNotice in the classic Header's account menu: the Impressum
// two-click rule (§5 DDG) and GDPR want these reachable from every page —
// infinite-scroll pages have no reachable footer, so they live here.
const LEGAL_LINKS = [
  { href: '/about', label: 'About' },
  { href: '/about/faq', label: 'FAQ' },
  { href: '/about/terms', label: 'Terms' },
  { href: '/about/privacy', label: 'Privacy' },
  { href: '/about/cookies', label: 'Cookies' },
  { href: '/about/affiliate-disclosure', label: 'Affiliate Disclosure' },
  { href: '/about/contact', label: 'Contact' },
];

export default function ArticleNav({ overPhoto }) {
  const [scrolled, setScrolled] = useState(false);
  const [section, setSection] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const ticking = useRef(false);
  // The progress line/plane move every frame — they're driven through refs
  // with direct style writes so the (backdrop-blurred) header doesn't go
  // through a React re-render per scroll frame.
  const lineRef = useRef(null);
  const planeRef = useRef(null);
  const sectionIndexRef = useRef(0);
  const { theme, setDarkMode, setLightMode } = useContext(UserContext);
  const plausible = usePlausible();

  // Over a cinematic masthead the nav starts transparent on the photo with
  // fixed light inks (the photo doesn't follow the theme), then adopts the
  // usual paper treatment as soon as the page scrolls. `overPhoto` comes
  // from server props and `scrolled` initializes false, so SSR HTML and
  // first client render agree.
  const overlayNow = Boolean(overPhoto) && !scrolled;
  const navLinkInk = overlayNow
    ? 'tw-text-[#F5F1E6] hover:tw-text-white'
    : 'tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] hover:tw-text-primary-700 dark:hover:tw-text-primary-300';
  const iconBtnInk = overlayNow
    ? 'tw-text-[rgba(245,241,230,0.8)] hover:tw-bg-white/15 hover:tw-text-white'
    : 'tw-text-[#5F6F69] dark:tw-text-[#93A69E] hover:tw-bg-[rgba(29,45,40,0.06)] hover:tw-text-[#1D2D28] dark:hover:tw-bg-white/10 dark:hover:tw-text-[#EBE6D9]';

  useEffect(() => {
    const headings = Array.from(
      document.querySelectorAll(
        '.tf-article h1[id], .tf-article h2[id], .tf-article h3[id]',
      ),
    );
    // Layout metrics are cached: reading scrollHeight/rects inside the scroll
    // handler forces layout. The page grows as lazy sections load, so a
    // ResizeObserver keeps the cache fresh.
    let maxScroll = 0;
    let headingTops = [];
    const measure = () => {
      maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const y = window.scrollY;
      headingTops = headings.map(h => h.getBoundingClientRect().top + y);
    };
    const update = () => {
      ticking.current = false;
      const y = window.scrollY;
      const progress = maxScroll > 0 ? Math.min(100, (y / maxScroll) * 100) : 0;
      if (lineRef.current)
        lineRef.current.style.width = `calc(${progress}% - 6px)`;
      if (planeRef.current) {
        planeRef.current.style.left = `calc(${progress}% - 8px)`;
        planeRef.current.style.visibility =
          progress > 0.5 ? 'visible' : 'hidden';
      }
      setScrolled(y > 12);
      // Scrollspy: the last heading that has scrolled past the nav.
      let index = 0;
      for (let i = 0; i < headingTops.length; i += 1) {
        if (headingTops[i] <= y + 96) index = i + 1;
        else break;
      }
      if (index !== sectionIndexRef.current) {
        sectionIndexRef.current = index;
        setSection(
          index
            ? { title: headings[index - 1].textContent, index }
            : null,
        );
      }
    };
    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        window.requestAnimationFrame(update);
      }
    };
    const onResize = () => {
      measure();
      onScroll();
    };
    measure();
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(document.body);
    }
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = e => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`${
          figtree.className
        } tw-fixed tw-inset-x-0 tw-top-0 tw-z-50 tw-transition-[background-color,box-shadow] tw-duration-300 ${
          scrolled
            ? 'tw-bg-[rgba(250,248,241,0.92)] dark:tw-bg-[rgba(14,26,22,0.92)] tw-backdrop-blur-md tw-shadow-[0_1px_0_0_rgba(29,45,40,0.08)] dark:tw-shadow-[0_1px_0_0_rgba(255,255,255,0.06)]'
            : 'tw-bg-transparent'
        }`}
      >
        <nav className="tw-mx-auto tw-flex tw-h-16 tw-max-w-7xl tw-items-center tw-justify-between tw-gap-3 tw-px-4 sm:tw-px-6 lg:tw-px-8">
          <Link
            href="/"
            className="tw-flex tw-shrink-0 tw-items-center tw-no-underline"
            aria-label="TravelFeed"
          >
            {/* The wordmark never fits next to all the nav controls on
                phones — below sm the square mark stands in (like the
                classic Header), inked for paper, dark and over-photo. */}
            <TfMark
              className={`tw-block sm:tw-hidden tw-h-7 tw-w-auto ${
                overlayNow
                  ? 'tw-text-white'
                  : 'tw-text-primary-600 dark:tw-text-white'
              }`}
            />
            <Image
              src={logoColor}
              alt="TravelFeed"
              className={
                overlayNow
                  ? 'tw-hidden'
                  : 'tw-hidden sm:tw-block tw-h-7 tw-w-auto dark:tw-hidden'
              }
              priority
            />
            <Image
              src="/header-logo.svg"
              alt="TravelFeed"
              width={162}
              height={28}
              className={`tw-h-7 tw-w-auto ${
                overlayNow ? 'tw-hidden sm:tw-block' : 'tw-hidden dark:sm:tw-block'
              }`}
              priority
            />
          </Link>

          {/* Center: nav links at rest, the current chapter while reading */}
          <div className="tw-hidden tw-min-w-0 tw-flex-1 tw-items-center tw-justify-center md:tw-flex">
            {section ? (
              <span className="tw-flex tw-min-w-0 tw-items-baseline tw-gap-2.5 tw-px-4">
                <span className="tw-shrink-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-[0.2em] tw-text-primary-600 dark:tw-text-primary-300">
                  {String(section.index).padStart(2, '0')}
                </span>
                <span className="tw-truncate tw-text-[14px] tw-font-semibold tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]">
                  {section.title}
                </span>
              </span>
            ) : (
              <span className="tw-hidden tw-items-center tw-gap-7 lg:tw-flex">
                <Link
                  href="/explore"
                  prefetch={false}
                  className={`tw-text-[15px] tw-font-medium tw-no-underline tw-transition-colors ${navLinkInk}`}
                >
                  Explore stories
                </Link>
                <Link
                  href="/destinations"
                  prefetch={false}
                  className={`tw-text-[15px] tw-font-medium tw-no-underline tw-transition-colors ${navLinkInk}`}
                >
                  Destinations
                </Link>
              </span>
            )}
          </div>

          <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1 sm:tw-gap-3">
            <button
              type="button"
              aria-label={
                theme === 'dark'
                  ? 'Switch to light mode'
                  : 'Switch to dark mode'
              }
              onClick={() =>
                theme === 'dark' ? setLightMode() : setDarkMode()
              }
              className={`tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-transition-colors ${iconBtnInk}`}
            >
              {theme === 'dark' ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  className="tw-h-[18px] tw-w-[18px]"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="4.5" />
                  <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5 5l1.6 1.6M17.4 17.4 19 19M19 5l-1.6 1.6M6.6 17.4 5 19" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="tw-h-[18px] tw-w-[18px]"
                  aria-hidden="true"
                >
                  <path d="M20.5 14.5A8.5 8.5 0 1 1 9.5 3.5a7 7 0 0 0 11 11z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={() => setLoginOpen(true)}
              className={`tw-cursor-pointer tw-whitespace-nowrap tw-border-0 tw-bg-transparent tw-px-2 tw-py-2 tw-text-sm tw-font-medium tw-transition-colors sm:tw-px-3 sm:tw-text-[15px] ${navLinkInk}`}
              style={{ fontFamily: 'inherit' }}
            >
              Log in
            </button>
            {loginOpen && (
              <LoginButton
                hideButtons
                loginOpen
                onClickClose={() => setLoginOpen(false)}
              />
            )}
            <Link
              href="/"
              prefetch={false}
              onClick={() => plausible('CTA: Article Nav')}
              className="tw-whitespace-nowrap tw-rounded-full tw-bg-primary-600 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-no-underline tw-shadow-[0_6px_20px_-8px_rgba(0,137,123,0.8)] tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-bg-primary-700 hover:tw-text-white sm:tw-px-5 sm:tw-py-2.5 sm:tw-text-[15px]"
            >
              Start your blog
            </Link>

            {/* More menu — counterpart of the classic Header's account menu */}
            <div className="tw-relative" ref={menuRef}>
              <button
                type="button"
                aria-label="More"
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(open => !open)}
                className={`tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-transition-colors ${iconBtnInk}`}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="tw-h-[18px] tw-w-[18px]"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="5" r="1.8" />
                  <circle cx="12" cy="12" r="1.8" />
                  <circle cx="12" cy="19" r="1.8" />
                </svg>
              </button>
              {menuOpen && (
                <div className="tw-absolute tw-right-0 tw-top-full tw-mt-3 tw-w-64 tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-p-4 tw-shadow-[0_24px_50px_-24px_rgba(29,45,40,0.45)]">
                  <div className="tw-flex tw-flex-col">
                    {[
                      { href: '/explore', label: 'Explore stories' },
                      { href: '/destinations', label: 'Destinations' },
                      { href: '/help', label: 'Help Center' },
                    ].map(({ href, label }) => (
                      <Link
                        key={href}
                        href={href}
                        prefetch={false}
                        onClick={() => setMenuOpen(false)}
                        className="tw-rounded-lg tw-px-2.5 tw-py-2 tw-text-[14px] tw-font-medium tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] tw-no-underline tw-transition-colors hover:tw-bg-[rgba(29,45,40,0.06)] dark:hover:tw-bg-white/10"
                      >
                        {label}
                      </Link>
                    ))}
                  </div>
                  <div className="tw-my-3 tw-border-0 tw-border-t tw-border-dashed tw-border-[#E7E0CF] dark:tw-border-[#26382F]" />
                  <div className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-px-2.5">
                    {LEGAL_LINKS.map(({ href, label }) => (
                      <Link
                        key={href}
                        href={href}
                        prefetch={false}
                        onClick={() => setMenuOpen(false)}
                        className="tw-text-[12px] tw-text-[#5F6F69] dark:tw-text-[#93A69E] tw-no-underline hover:tw-text-primary-700 dark:hover:tw-text-primary-300 hover:tw-underline"
                      >
                        {label}
                      </Link>
                    ))}
                  </div>
                  <p className="tw-mb-0 tw-mt-3 tw-px-2.5 tw-font-mono tw-text-[10px] tw-text-[#8A958F]">
                    © {new Date().getFullYear()} TravelFeed · A Hive frontend
                  </p>
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Flight-route reading progress: line and plane share one strip and
            one vertical center, so the plane sits exactly on the route. */}
        <div
          aria-hidden="true"
          className="tw-pointer-events-none tw-absolute tw-inset-x-0 -tw-bottom-2 tw-h-4"
        >
          <div
            ref={lineRef}
            className="tw-absolute tw-left-0 tw-top-1/2 tw-h-[2px] -tw-translate-y-1/2 tw-bg-gradient-to-r tw-from-primary-600/30 tw-to-primary-600 dark:tw-from-primary-300/30 dark:tw-to-primary-300"
            style={{ width: 'calc(0% - 6px)' }}
          />
          <span
            ref={planeRef}
            className="tw-absolute tw-top-1/2 tw-block -tw-translate-y-1/2"
            style={{ left: 'calc(0% - 8px)', visibility: 'hidden' }}
          >
            <PlaneIcon className="tw-block tw-h-4 tw-w-4 tw-rotate-90 tw-text-primary-700 dark:tw-text-primary-300" />
          </span>
        </div>
      </header>
      {/* Over a cinematic masthead the page starts at y=0 under the
          transparent nav — no spacer; the masthead's top scrim keeps the
          nav legible. Otherwise the spacer is 65px — the classic Header's
          exact spacer height, so the member swap on hydration is
          pixel-stable. */}
      {!overPhoto && <div className="tw-h-[65px]" />}
    </>
  );
}
